import React from 'react';
import moment from 'moment';
import {
  Row,
  Statistic,
  Col,
  Divider,
  Modal,
  Spin,
  Progress,
  Tag,
  Empty,
  Button,
} from 'antd';
import { useFetchCSVMetrics } from 'query/metrics';
import { calPercent, UploadedCSV, QueryFilter } from './utils';
import { useGetCSVWatchStatus, useGetOneCSVSummary } from 'query/mail';
import { useUnWatchCSVMail, useWatchCSVMail } from 'mutation/mail';
import CSVAction from './CSVAction';
import { EMAIL_SERVICES } from 'constants/common';
import sum from 'lodash/sum';

type CSVSummaryProps = {
  data: UploadedCSV,
  myEmail: string,
  myId: number,
  selectedId: number,
  queryFilter: QueryFilter,
  canInterruptMailSending: boolean,
  onClickDetails: () => () => void,
};

export default function CSVSummary(props: CSVSummaryProps) {
  const { data: queryData, loading } = useGetOneCSVSummary(props.selectedId);
  return (
    <CSVSummaryDisplay
      {...props}
      data={{ ...props.data, ...queryData }}
      loading={loading}
    />
  );
}

function CSVSummaryDisplay(props: CSVSummaryProps) {
  const { data, myEmail, myId } = props;

  if (!data?.id)
    return (
      <Modal
        bodyStyle={{ marginBottom: -30 }}
        closable
        footer={null}
        style={{ top: 20 }}
        title={null}
        visible={props.show}
        width="60%"
        onCancel={props.onClose}
      >
        <div style={{ display: 'grid', placeItems: 'center', height: '30vh' }}>
          {props.loading ? <Spin size="large" /> : <Empty />}
        </div>
      </Modal>
    );

  const isScheduled = data.status === 'scheduled';
  const isPaused = data.isPaused;
  const isAborted = data.isAborted;
  const isProcessing = data.status === 'processing';
  const isCompleted = data.status === 'completed';

  const isAuthorizedAccountCampaign =
    data.esp === EMAIL_SERVICES.GMAIL || data.esp === EMAIL_SERVICES.MICROSOFT;

  const isInterrupted = isPaused || isAborted;
  const canChangeStatus = !isAborted && !isCompleted;

  const totalSkipped = sum([
    data.unsubscribedCount,
    data.throttledCount,
    data.suspendedDevsCount,
    data.invalidCount,
    data.cappedCount,
    data.duplicateCount,
    data.inactiveAccountCount,
  ]);

  const totalProcessed = sum([data.sentCount, data.failedCount, totalSkipped]);
  const progressPercentage = calPercent(data.totalRows, totalProcessed);

  const displayProgress = isProcessing && progressPercentage < 100;

  let title;
  if (isAborted) title = 'Aborted';
  else if (isPaused) title = 'Paused';

  return (
    <Modal
      bodyStyle={{ marginBottom: -30 }}
      style={{ top: 20 }}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {canChangeStatus && (
            <CSVAction
              canInterruptMailSending={props.canInterruptMailSending}
              data={data}
              hideDetails
              myId={props.myId}
              queryFilter={props.queryFilter}
              onClickDetails={props.onClickDetails}
            />
          )}
          <div>{title}</div>
          {canChangeStatus && (
            <div style={{ marginBottom: -10, marginRight: 30 }}>
              <WatchCSV id={data.id} />
            </div>
          )}
        </div>
      }
      visible={props.show}
      width="60%"
      onCancel={props.onClose}
      onOk={props.onClose}
    >
      <div style={{ padding: 10 }}>
        {displayProgress && (
          <div style={{ marginTop: 15, marginBottom: 15, marginRight: 20 }}>
            <Progress
              percent={Number(progressPercentage.toFixed(1))}
              status="active"
              successPercent={Number(
                calPercent(data.totalRows, data.sentCount).toFixed(1),
              )}
            />
          </div>
        )}

        <Row gutter={10}>
          <Col span={12}>
            <Statistic
              title="Name"
              value={data.originalname}
              valueStyle={{ fontSize: 15 }}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Owner"
              value={data.owner || (data.user?.id === myId ? myEmail : '')}
              valueStyle={{ fontSize: 15 }}
            />
          </Col>
        </Row>

        <Row gutter={10} style={{ marginTop: 15 }}>
          <Col span={12}>
            <Statistic
              title="Uploaded On"
              value={data.uploadedTime}
              valueStyle={{ fontSize: 15 }}
            />
          </Col>
          <Col span={12}>
            {isScheduled ? (
              <Statistic
                title={data.scheduledTime ? 'Scheduled Time' : 'Scheduled'}
                value={
                  data.scheduledTime
                    ? moment(new Date(data.scheduledTime)).format(
                        'YYYY-MM-DD HH:mm',
                      )
                    : 'Yes'
                }
                valueStyle={{ fontSize: 15 }}
              />
            ) : data.totalRows ? (
              <Statistic
                title="Total Recipients"
                value={data.totalRows}
                valueStyle={{ fontSize: 15 }}
              />
            ) : (
              <></>
            )}
          </Col>
        </Row>

        {!!data.totalRows && (
          <>
            <Separator />
            <Row gutter={10}>
              <PercentageItem
                title="Sent"
                total={data.totalRows}
                value={data.sentCount}
                valueStyle={{ color: 'green' }}
              />
              <PercentageItem
                title="Failed"
                total={data.totalRows}
                value={data.failedCount}
                valueStyle={data.failedCount ? { color: 'red' } : {}}
              />
              <PercentageItem
                title="Unsubscribed"
                total={data.totalRows}
                value={data.unsubscribedCount}
              />
              <PercentageItem
                title="Throttled"
                total={data.totalRows}
                value={data.throttledCount}
              />
              <PercentageItem
                title="Invalid Emails"
                total={data.totalRows}
                value={data.invalidCount}
              />
              <PercentageItem
                title="Duplicate Emails"
                total={data.totalRows}
                value={data.duplicateCount}
              />
              <PercentageItem
                title={isInterrupted ? title : 'In Queue'}
                total={data.totalRows}
                value={data.totalRows - totalProcessed}
                valueStyle={{ color: isInterrupted ? 'Red' : undefined }}
              />
              <PercentageItem
                title="Capped"
                total={data.totalRows}
                value={data.cappedCount}
              />
              <PercentageItem
                title="Suspended Devs"
                total={data.totalRows}
                value={data.suspendedDevsCount}
              />
              {isAuthorizedAccountCampaign && (
                <>
                  <PercentageItem
                    title="Suspended"
                    total={data.totalRows}
                    value={data.suspendedCount}
                  />
                  <PercentageItem
                    title="Revoked"
                    total={data.totalRows}
                    value={data.revokedCount}
                  />
                </>
              )}
            </Row>
          </>
        )}

        {!!data.sentCount && !!props.selectedId && (
          <PerformanceMetrics
            selectedId={props.selectedId}
            sentCount={data.sentCount}
          />
        )}
      </div>
    </Modal>
  );
}

const PerformanceMetrics = props => {
  const { data, isFetching, error } = useFetchCSVMetrics(props.selectedId);

  return (
    <>
      <Separator />
      <Row gutter={10}>
        <Spin spinning={isFetching}>
          <>
            <PercentageItem
              error={error}
              title="Delivered"
              total={props.sentCount}
              value={data.delivered_count}
            />
            <PercentageItem
              error={error}
              title="Replied"
              total={props.sentCount}
              value={data.replied_count}
            />
            <PercentageItem
              error={error}
              noPercentage
              title="Opened"
              total={props.sentCount}
              value={data.opened_count}
            />
            <PercentageItem
              error={error}
              title="Opened (uniq)"
              total={props.sentCount}
              value={data.opened_uniq_count}
            />
            <PercentageItem
              error={error}
              noPercentage
              title="Click"
              total={props.sentCount}
              value={data.click_through_count}
            />
            <PercentageItem
              error={error}
              title="Click (uniq)"
              total={props.sentCount}
              value={data.click_through_uniq_count}
            />
          </>
        </Spin>
      </Row>
      <Separator label="Failure Metrics" />
      <Row gutter={10}>
        <Spin spinning={isFetching}>
          <>
            <PercentageItem
              error={error}
              title="Spam"
              total={props.sentCount}
              value={data.spam_count}
            />
            <PercentageItem
              error={error}
              title="Bounce"
              total={props.sentCount}
              value={data.bounce_count}
            />
            <PercentageItem
              error={error}
              title="Dropped"
              total={props.sentCount}
              value={data.dropped_count}
            />
            <PercentageItem
              error={error}
              title="Deferred"
              total={props.sentCount}
              value={data.deferred_count}
            />
            <PercentageItem
              error={error}
              title="Unsubscribe"
              total={props.sentCount}
              value={data.unsubscribe_count}
            />
          </>
        </Spin>
      </Row>
    </>
  );
};

export const PercentageItem = props => {
  const value = props.value || 0;
  return (
    <Col span={6} onClick={props.onClick}>
      <Statistic
        style={{
          marginBottom: 20,
          cursor: props.onClick ? 'pointer' : undefined,
        }}
        suffix={
          props.noPercentage || props.error
            ? ''
            : ` (${calPercent(props.total, value).toFixed(1)}%)`
        }
        title={
          <span
            style={
              props.onClick
                ? {
                    color: 'blue',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }
                : {}
            }
          >
            {props.title}
          </span>
        }
        value={props.error ? 'N/A' : value}
        valueStyle={props.error ? { fontSize: 10 } : props.valueStyle}
      />
    </Col>
  );
};

const Separator = props => (
  <div style={{ marginBottom: 15, marginTop: 15 }}>
    {props.label && (
      <div>
        <Tag>{props.label}</Tag>
      </div>
    )}
    <div>
      <Divider style={{ margin: 1, padding: 0 }} />
      <Divider style={{ margin: 1, padding: 0 }} />
      <Divider style={{ margin: 1, padding: 0 }} />
    </div>
  </div>
);

const WatchCSV = ({ id }) => {
  const {
    data: csvWatchStatus,
    isLoading: isFetchingWatchStatus,
  } = useGetCSVWatchStatus(id);
  const { mutate: unWatchCSVMail, isLoading: isUnWatching } = useUnWatchCSVMail(
    id,
  );
  const { mutate: watchCSVMail, isLoading: isWatching } = useWatchCSVMail(id);

  const isWatched = !!csvWatchStatus?.active;

  return (
    <Button
      icon={isWatched ? 'eye' : 'eye-invisible'}
      loading={isFetchingWatchStatus || isUnWatching || isWatching}
      size="small"
      style={
        isWatched
          ? {
              backgroundColor: 'dodgerblue',
              color: 'white',
            }
          : {}
      }
      onClick={() => (isWatched ? unWatchCSVMail(id) : watchCSVMail(id))}
    >
      {isWatched ? 'Watching' : 'Click to watch'}
    </Button>
  );
};
